// src/utils/networkDetector.js
import { useState, useEffect, useRef, useCallback } from 'react';

export const useNetworkStatus = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    useEffect(() => {
        const handleOnline = () => {
            console.log('[Network] ONLINE');
            setIsOnline(true);
        };

        const handleOffline = () => {
            console.log('[Network] OFFLINE');
            setIsOnline(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return isOnline;
};

// Enhanced hook that tracks history + dispatches a custom event
// so child components can auto-refresh data on transitions
export const useNetworkStatusWithHistory = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [wasOffline, setWasOffline] = useState(false);
    const [transition, setTransition] = useState(null); // 'online-to-offline' | 'offline-to-online' | null
    const prevOnline = useRef(navigator.onLine);

    useEffect(() => {
        const handleOnline = () => {
            const wasOff = !prevOnline.current;
            console.log('[Network] ONLINE (was offline:', wasOff, ')');

            if (wasOff) {
                setWasOffline(true);
                setTransition('offline-to-online');

                // Dispatch custom event so any component can react
                window.dispatchEvent(new CustomEvent('network-transition', {
                    detail: { type: 'offline-to-online', timestamp: Date.now() }
                }));
            }

            prevOnline.current = true;
            setIsOnline(true);
        };

        const handleOffline = () => {
            const wasOn = prevOnline.current;
            console.log('[Network] OFFLINE (was online:', wasOn, ')');

            if (wasOn) {
                setTransition('online-to-offline');

                // Dispatch custom event
                window.dispatchEvent(new CustomEvent('network-transition', {
                    detail: { type: 'online-to-offline', timestamp: Date.now() }
                }));
            }

            prevOnline.current = false;
            setIsOnline(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const clearWasOffline = useCallback(() => {
        setWasOffline(false);
        setTransition(null);
    }, []);

    return { isOnline, wasOffline, transition, clearWasOffline };
};

// =========================================================
// Hook for child components to auto-refresh on transitions
// Usage: useRefreshOnNetworkChange(() => fetchMyData());
// =========================================================
export const useRefreshOnNetworkChange = (refreshCallback) => {
    useEffect(() => {
        const handler = (event) => {
            const { type } = event.detail;
            console.log('[NetworkRefresh] Transition detected:', type, '→ refreshing data');

            if (typeof refreshCallback === 'function') {
                // Small delay to let sync/server switch settle
                setTimeout(() => {
                    refreshCallback(type);
                }, 1500);
            }
        };

        window.addEventListener('network-transition', handler);
        return () => window.removeEventListener('network-transition', handler);
    }, [refreshCallback]);
};